/**
 * Game Store Helpers
 * 
 * Utility functions used throughout the game store.
 */

import { 
  GameSettings, 
  Statistics, 
  LevelingConfig, 
  Achievement, 
  Card, 
  Suit, 
  Rank,
  TableSimDeckState,
  SpeedDrillDeckState
} from '../../types';
import { createSeededShoe, isBlackjack, calculateTrueCount, getDecksRemaining } from '../../utils/deck';
import { getCardValue } from '../../utils/countingSystems';
import { DEFAULT_ACHIEVEMENTS } from '../../utils/achievements';
import { queueStatsSave } from '../../services/userState';
import { DEFAULT_LEVELING, XP_REWARDS, DEFAULT_TABLE_CONFIG, createSeats } from './defaults';
import type { GameStore, TrainingDeckPersist, TrainingSessionPersist, TableSessionPersist } from './types';

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Calculate level from total XP
 */
export function calculateLevel(totalXP: number, leveling?: LevelingConfig): number {
  const config = leveling || DEFAULT_LEVELING;
  let level = 1;
  let xpNeeded = config.baseXP;
  let accumulatedXP = 0;

  while (accumulatedXP + xpNeeded <= totalXP && level < config.maxLevel) {
    accumulatedXP += xpNeeded;
    level++;
    xpNeeded = config.baseXP + (level - 1) * config.increment;
  }

  return level;
}

/**
 * Combine default and custom achievement definitions
 */
export function combineAchievementDefinitions(settings: GameSettings): Achievement[] {
  return [...DEFAULT_ACHIEVEMENTS, ...(settings.customAchievements || [])];
}

/**
 * Compute XP reward for an achievement
 */
export function computeAchievementReward(achievement: Achievement): number {
  if (achievement.rewardXP !== undefined) return achievement.rewardXP;
  const base = XP_REWARDS.achievementUnlocked;
  if (achievement.requirements && achievement.requirements.length > 0) {
    const total = achievement.requirements.reduce((sum, req) => sum + (req.value || 0), 0);
    return clamp(base + Math.round(total * 0.5), base, 500);
  }
  const req = achievement.requirement || 0;
  return clamp(base + Math.round(req * 0.5), base, 500);
}

/**
 * Create a card with the given properties
 */
export function makeCard(id: string, rank: Rank, suit: Suit, faceUp: boolean = true): Card {
  return { id, rank, suit, faceUp };
}

/**
 * Stack specific cards on top of a deck (for debug purposes)
 */
export function stackDeck(baseDeck: Card[], stack: Card[]): Card[] {
  const trimmed = baseDeck.filter(c => !stack.some(s => s.id === c.id));
  return [...trimmed.slice(0, Math.max(0, trimmed.length - stack.length)), ...stack];
}

/**
 * Build a stack for table simulator in the correct deal order
 * Cards are popped from the end: seat0 first, seat1 first, user first, dealer up, etc.
 */
export function buildTableStack(
  initial: {
    seat0First: Card;
    seat1First: Card;
    userFirst: Card;
    dealerUp: Card;
    seat0Second: Card;
    seat1Second: Card;
    userSecond: Card;
    dealerHole: Card;
  },
  futureCards: Card[] = []
): Card[] {
  const extras = [...futureCards].reverse();
  return [
    ...extras,
    initial.dealerHole,
    initial.userSecond,
    initial.seat1Second,
    initial.seat0Second,
    initial.dealerUp,
    initial.userFirst,
    initial.seat1First,
    initial.seat0First
  ];
}

/**
 * Persist statistics to Firestore
 */
export function persistStats(stats: Statistics): void {
  queueStatsSave(stats);
}

/**
 * Update statistics with current bankroll
 */
export function updateStatsWithBankroll(stats: Statistics, bankroll: number): Statistics {
  return { ...stats, currentBankroll: bankroll };
}

// Note: Hand history is now persisted to localStorage, not Firestore
// The persistHistory function is no longer needed here - handled in gameStore subscriber

/**
 * Build default training deck state with seeded shuffle
 */
export function buildDefaultTrainingDeck(settings: GameSettings): TrainingDeckPersist {
  const { deck, seed } = createSeededShoe(settings.numDecks);
  return {
    deck,
    dealtCards: [],
    deckSeed: seed,
    runningCount: 0,
    trueCount: 0
  };
}

/**
 * Build default table deck state with seeded shuffle
 */
export function buildDefaultTableDeck(settings: GameSettings): TableSimDeckState {
  const { deck, seed } = createSeededShoe(settings.numDecks);
  return {
    deck,
    dealtCards: [],
    deckSeed: seed,
    runningCount: 0,
    trueCount: 0,
    dealerHand: { cards: [], bet: 0 }
  };
}

/**
 * Build default speed drill deck state with seeded shuffle
 */
export function buildDefaultSpeedDrillDeck(settings: GameSettings): SpeedDrillDeckState {
  const { deck, seed } = createSeededShoe(settings.numDecks);
  return {
    deck,
    dealtCards: [],
    deckSeed: seed,
    runningCount: 0
  };
}

/**
 * Load training deck (creates fresh state)
 */
export function loadTrainingDeck(settings: GameSettings): TrainingDeckPersist {
  return buildDefaultTrainingDeck(settings);
}

/**
 * Load table simulator deck (creates fresh state)
 */
export function loadTableSimDeck(settings: GameSettings): TableSimDeckState {
  return buildDefaultTableDeck(settings);
}

/**
 * Build default training session state
 */
export function buildDefaultTrainingSession(settings: GameSettings, minBet: number): TrainingSessionPersist {
  const deckState = loadTrainingDeck(settings);
  return {
    ...deckState,
    playerHands: [{ cards: [], bet: 0 }],
    currentHandIndex: 0,
    dealerHand: { cards: [], bet: 0 },
    isPlaying: false,
    isDealing: false,
    playerTurnComplete: false,
    roundComplete: false,
    insuranceOffered: false,
    insuranceResult: null,
    insuranceBet: 0,
    lastBaseBet: minBet,
    autoDeal: false,
    dealSpeed: 1000,
    lastRoundNet: 0,
    lastBetAnalysis: null,
    lastInsuranceAnalysis: null,
    lastDecisionAnalysis: null,
    debugSettingsRestore: null
  };
}

/**
 * Load training session (creates fresh state)
 */
export function loadTrainingSession(settings: GameSettings, minBet: number): TrainingSessionPersist {
  return buildDefaultTrainingSession(settings, minBet);
}

/**
 * Build default table session state
 */
export function buildDefaultTableSession(settings: GameSettings): TableSessionPersist {
  return {
    tableSimDeck: loadTableSimDeck(settings),
    seats: createSeats(DEFAULT_TABLE_CONFIG),
    tableState: {
      dealingPhase: 'idle',
      currentSeatIndex: -1,
      currentHandIndex: 0,
      isAutoPlaying: false,
      insuranceOffered: false,
      insuranceTaken: false
    }
  };
}

/**
 * Load table session (creates fresh state)
 */
export function loadTableSession(settings: GameSettings): TableSessionPersist {
  return buildDefaultTableSession(settings);
}

// Mutable flag for suppressing settings persistence during debug
export let suppressSettingsPersist = false;
export function setSuppressSettingsPersist(value: boolean): void {
  suppressSettingsPersist = value;
}

/**
 * Restore debug settings if needed
 */
export function restoreDebugSettingsIfNeeded(
  getFn: () => GameStore, 
  setFn: (partial: Partial<GameStore>) => void
): void {
  const state = getFn();
  if (state.debugSettingsRestore) {
    setSuppressSettingsPersist(true);
    setFn({ settings: state.debugSettingsRestore, debugSettingsRestore: null });
  }
}

/**
 * Reveal dealer hole card and resolve blackjack if applicable
 * Returns true if dealer has blackjack and resolution was triggered
 */
export function revealDealerHoleAndMaybeResolveBJ(
  getFn: () => GameStore,
  setFn: (partial: Partial<GameStore> | ((state: GameStore) => Partial<GameStore>)) => void
): boolean {
  const state = getFn();
  const holeCard = state.dealerHand.cards[1];
  if (!holeCard) return false;

  const dealerHasBlackjack = isBlackjack(state.dealerHand.cards);
  if (!dealerHasBlackjack) return false;

  // Delay to make the reveal feel smoother and freeze controls
  setFn({
    isDealing: true,
    playerTurnComplete: true
  });

  setTimeout(() => {
    const latest = getFn();
    const latestHole = latest.dealerHand.cards[1];
    if (!latestHole || latestHole.faceUp) return;

    const dealerCards = [...latest.dealerHand.cards];
    dealerCards[1] = { ...dealerCards[1], faceUp: true };

    const newRunningCount = latest.runningCount + getCardValue(dealerCards[1].rank, latest.settings.countingSystem);
    const decksRemaining = getDecksRemaining(latest.deck.length);
    const newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    setFn({
      dealerHand: { ...latest.dealerHand, cards: dealerCards },
      runningCount: newRunningCount,
      trueCount: newTrueCount
    });

    // Small extra delay to let the flip render before resolving
    setTimeout(() => getFn().resolveDealerHand(), 200);
  }, 1000);

  return true;
}

